import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { catchError, map, of, type Observable } from 'rxjs';
import type { DropdownOption } from '@zag/ui';

export interface MicrosoftDirectoryConfigInput {
  enabled?: boolean;
  tenantId?: string;
  clientId?: string;
  clientSecret?: string | null;
  scopes?: string[];
  hasClientSecret?: boolean;
}

export interface TeamsInvitationDefaultsInput {
  enabled?: boolean;
  recipientPropertyKeys?: string[];
  confirmationTemplateKey?: string | null;
}

export interface DirectoryConfig {
  microsoft?: MicrosoftDirectoryConfigInput;
  teamsInvitationDefaults?: TeamsInvitationDefaultsInput;
}

export interface DirectoryConnectionTestPayload {
  provider: 'microsoft';
}

export interface DirectoryConnectionTestResult {
  success: boolean;
  message: string;
}

export interface CalendarSlotCustomPropertyDefinition {
  key: string;
  label: string;
  propertyType: string;
  config: Record<string, unknown> | null;
}

export interface EmailTemplateConfigRow {
  templateKey?: string | null;
  templateName?: string | null;
}

// Mirrors ESSENTIAL_TEMPLATE_KEYS in @zag/templates/api: auth templates can't be repurposed as Teams confirmations.
const RESERVED_TEMPLATE_KEYS = new Set(['auth.invitation', 'auth.reset-password']);

/**
 * Service for the directory integration settings
 * (`/api/v1/settings/directory/config`) and its connection-test endpoint.
 */
@Injectable({ providedIn: 'root' })
export class DirectorySettingsService {
  private readonly http = inject(HttpClient);
  private readonly configPath = '/api/v1/settings/directory/config';
  private readonly connectionTestsPath = '/api/v1/settings/directory/connection-tests';

  getConfig(): Observable<DirectoryConfig> {
    return this.http.get<DirectoryConfig>(this.configPath);
  }

  patchConfig(payload: DirectoryConfig): Observable<DirectoryConfig> {
    return this.http.patch<DirectoryConfig>(this.configPath, payload);
  }

  testConnection(payload: DirectoryConnectionTestPayload): Observable<DirectoryConnectionTestResult> {
    return this.http.post<DirectoryConnectionTestResult>(this.connectionTestsPath, payload);
  }

  getCalendarSlotCustomPropertyDefinitions(): Observable<CalendarSlotCustomPropertyDefinition[]> {
    return this.http.get<CalendarSlotCustomPropertyDefinition[]>('/api/v1/core/custom-property-definitions', {
      params: { entityType: 'calendar-slot' },
    });
  }

  getEmailTemplateOptions(): Observable<DropdownOption<string>[]> {
    return this.http.get<EmailTemplateConfigRow[]>('/api/v1/email/template-configs').pipe(
      map(rows => {
        const seen = new Set<string>();
        const options: DropdownOption<string>[] = [];
        for (const row of Array.isArray(rows) ? rows : []) {
          const key = row?.templateKey;
          if (typeof key !== 'string' || key.length === 0 || seen.has(key) || RESERVED_TEMPLATE_KEYS.has(key)) continue;
          seen.add(key);
          options.push({ value: key, label: row?.templateName ?? key });
        }
        return options;
      }),
      catchError(() => of([] as DropdownOption<string>[]))
    );
  }
}
